// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 20,
  right: 80,
  bottom: 25,
  left: 35,
};

const parser = d3.timeParse('%Y-%m-%d'); // https://github.com/d3/d3-time-format;

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let muskAnnouncement;
let muskAquisition;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%m/%d/%Y');
const recessionData = [
  { start: '7/1/1990', end: '3/1/1991' },
  { start: '3/1/2001', end: '11/1/2001' },
  { start: '12/1/2007', end: '6/1/2009' },
  { start: '2/1/2020', end: '4/1/2020' },
];

recessionData.forEach((d) => {
  d.start = rParser(d.start);
  d.end = rParser(d.end);
});

d3.json('https://ix.cnn.io/data/mortgage-rates/fred-30-year-fixed-mortgage-rates.json').then((data) => {
  rawData = data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    value: +d.value,
  }));

  chartData = rawData.filter(d => d.date > pTime('1999-12-31'));

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  maxValue = d3.max(chartData, d => d.value);

  lastData = chartData.filter(d => d.date == maxDomain)[0];

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 5 : 5;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%b ’%y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, x) => (i == x.length - 1 ? `${d3.format('.0f')(d)}%` : `${d3.format('.0f')(d)}`));

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const line = g.append('g')
    .attr('class', 'line');

  line.append('path')
    .datum(chartData)
    .attr('stroke', d => '#7c4ea5')
    .attr('stroke-width', '2')
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();

  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

  annotation.append('circle')
    .attr('r', '4')
    .attr('fill', '#7c4ea5')
    .attr('stroke', 'none');

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text(d3.timeFormat('%b %-d')(lastData.date));

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text(`${d3.format('.1f')(lastData.value)}%`)
    .attr('dy', '1.2em');

  window.pymChild.sendHeight();
}
